/**
 * Temporary bridge between cacheEvents system and React Query
 * Part of Stage 4 refactoring to connect legacy cache events to queryClient
 * Will be removed when cache events are fully integrated into queryClient
 */

import { useEffect } from 'react';
import { cacheEvents } from '@/utils/cacheEvents';
import { queryClient } from '@/services/queryClient';

// Type for Vite's import.meta.env
interface ImportMetaEnv {
  readonly VITE_ENABLE_CACHE_EVENTS_BRIDGE?: string;
  readonly DEV?: boolean;
  readonly MODE?: string;
  readonly PROD?: boolean;
  readonly SSR?: boolean;
  [key: string]: string | boolean | undefined;
}

interface ImportMeta {
  readonly env: ImportMetaEnv;
}

// Cache event types
type CacheEventType =
  | 'projects-invalidated'
  | 'users-invalidated'
  | 'stats-invalidated'
  | 'organizations-invalidated';

interface CacheEventDetail {
  type: CacheEventType;
  tags: string[];
  timestamp: number;
}

/**
 * Hook that bridges cacheEvents to queryClient invalidations
 * Maps cache invalidation events to appropriate React Query invalidation calls
 */
export function useCacheEventsBridge() {
  useEffect(() => {
    // Check if bridge is enabled via environment variable (defaults to true in development)
    // Vite exposes env via import.meta.env; custom flags must be VITE_* prefixed
    const meta = import.meta as ImportMeta;
    const rawFlag = meta.env?.VITE_ENABLE_CACHE_EVENTS_BRIDGE;
    const isEnabled =
      rawFlag !== undefined
        ? String(rawFlag).toLowerCase() !== 'false'
        : meta.env?.DEV === true;

    if (!isEnabled) {
      devLog('CacheEventsBridge: Disabled via environment variable');
      return;
    }

    if (meta.env?.DEV) {
      devLog('CacheEventsBridge: Initializing bridge between cacheEvents and queryClient');
    }

    const handleCacheInvalidation = (event: CustomEvent<CacheEventDetail>) => {
      const { type, tags } = event.detail;

      if (meta.env?.DEV) {
        devLog(`CacheEventsBridge: Received ${type} event with tags:`, tags);
      }

      switch (type) {
        case 'projects-invalidated':
          // Invalidate all project-related queries
          queryClient.invalidateQueries({
            predicate: (query) => {
              const key = query.queryKey;
              return Array.isArray(key) && key[0] === 'projects';
            }
          });
          if (meta.env?.DEV) devLog('CacheEventsBridge: Invalidated all project queries');
          break;

        case 'users-invalidated':
          // Invalidate user-related queries (primarily for admin views)
          queryClient.invalidateQueries({
            predicate: (query) => {
              const key = query.queryKey;
              return Array.isArray(key) && (
                key[0] === 'admin' ||
                (key[0] === 'user' && key.length > 1)
              );
            }
          });
          if (meta.env?.DEV) devLog('CacheEventsBridge: Invalidated user and admin queries');
          break;

        case 'organizations-invalidated':
          // Invalidate organization-related queries
          queryClient.invalidateQueries({
            predicate: (query) => {
              const key = query.queryKey;
              return Array.isArray(key) && (
                key[0] === 'organization' ||
                key[0] === 'organizations'
              );
            }
          });
          if (meta.env?.DEV) devLog('CacheEventsBridge: Invalidated organization queries');
          break;

        case 'stats-invalidated':
          // Invalidate stats queries (primarily admin stats)
          queryClient.invalidateQueries({
            predicate: (query) => {
              const key = query.queryKey;
              return Array.isArray(key) && (
                (key[0] === 'admin' && key[1] === 'stats') ||
                key[0] === 'stats'
              );
            }
          });
          if (meta.env?.DEV) devLog('CacheEventsBridge: Invalidated stats queries');
          break;

        default:
          if (meta.env?.DEV) devLog(`CacheEventsBridge: Unknown event type: ${type}`);
      }
    };

    // Subscribe to cache invalidation events
    cacheEvents.on(handleCacheInvalidation);

    if (meta.env?.DEV) devLog('CacheEventsBridge: Successfully subscribed to cache invalidation events');

    // Cleanup subscription on unmount
    return () => {
      cacheEvents.off(handleCacheInvalidation);
      if (meta.env?.DEV) devLog('CacheEventsBridge: Cleanup - unsubscribed from cache invalidation events');
    };
  }, []);
}
